import {Component, CUSTOM_ELEMENTS_SCHEMA, DestroyRef, effect, inject, OnInit, signal,} from '@angular/core';
import {Router} from '@angular/router';
import {LogoCache} from '../../../service/logo-cache';
import {AsyncPipe, NgClass, NgTemplateOutlet} from '@angular/common';
import {Observable} from 'rxjs';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {SectorDto} from '../../../dto/sector-dto';
import {SidebarRoute} from '../../../domain/sidebar-route';
import {HeaderBackConfig} from '../../../domain/header-back-config';
import {SelectSectorService} from '../../../service/sector-selection';
import {Sector} from '../../../service/sector';
import {environment} from '../../../../environments/environment';
import {Alert, AlertService, AlertType} from '@innovactions/frontend-library';
import {Role} from '../../../enum/role';
import {UserProfileManagement} from '../../user/user-profile-management/user-profile-management';
import {CurrentUserService} from '../../../service/current-user';
import {DashboardViewService} from '../../../service/dashboard-view';
import {getVisibleRoutes} from './sidebar-route-registry';
import {NotificationsOverview} from '../../notifications/notifications-overview/notifications-overview';
import {Avatar} from '../avatar/avatar';
import {SidebarSlidePanelSlot} from '../../../service/sidebar-slide-panel-slot';

@Component({
  selector: 'app-sidebar',
  imports: [
    AsyncPipe,
    NgClass,
    NgTemplateOutlet,
    UserProfileManagement,
    NotificationsOverview,
    Avatar,
  ],
  templateUrl: './sidebar.html',
  styleUrl: './sidebar.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class Sidebar implements OnInit {
  static readonly hideSidebar = signal(false);
  static readonly minimalHeader = signal(false);
  static readonly headerBack = signal<HeaderBackConfig | undefined>(undefined);
  protected readonly currentUserService = inject(CurrentUserService);
  protected readonly slidePanelSlot = inject(SidebarSlidePanelSlot);
  private readonly destroyRef = inject(DestroyRef);
  private readonly selectSectorService = inject(SelectSectorService);
  private readonly dashboardViewService = inject(DashboardViewService);

  protected readonly hideSidebar = Sidebar.hideSidebar;
  protected readonly minimalHeader = Sidebar.minimalHeader;
  protected readonly headerBack = Sidebar.headerBack;
  protected logo = signal<Observable<string> | undefined>(undefined);
  protected hoveredRoute = signal<string | undefined>(undefined);
  protected sectors: SectorDto[] = [];
  protected readonly selectedSectorId = this.selectSectorService.selectedSectorId;
  protected readonly selectedCompanyId = this.selectSectorService.selectedCompanyId;
  protected currentUserId = signal<string | undefined>(undefined);
  protected selectedSectorName = '';
  protected showProfileModal = signal<boolean>(false);
  protected unreadNotifications = signal<number>(0);

  constructor(
    private readonly logoCacheService: LogoCache,
    private readonly router: Router,
    private readonly sectorService: Sector,
    private readonly alertService: AlertService,
  ) {
    effect(() => {
      this.currentUserId.set(this.currentUserService.appUser()?.id ?? undefined);
    });
  }

  ngOnInit() {
    this.logo.set(this.logoCacheService.getLogo());
    this.loadSectors();
  }

  protected logout() {
    this.currentUserService.logout();
  }

  protected closeSidebar() {
    const closeBtn = document.querySelector(
      '[command="close"][commandfor="sidebar"]',
    ) as HTMLElement;
    closeBtn?.click();
  }

  protected isRouteActive(route: SidebarRoute & {id: string}): boolean {
    const targetRoute = this.buildRoute(route);
    const isActive = this.router.isActive(targetRoute, {
      paths: 'exact',
      queryParams: 'ignored',
      fragment: 'ignored',
      matrixParams: 'ignored',
    });
    return isActive && (!route.dashboardView
      || this.dashboardViewService.view === route.dashboardView);
  }

  protected onRouteClick(route: SidebarRoute & {id: string}, event: Event): void {
    if (route.dashboardView) {
      this.router.navigateByUrl(route.route, {
        info: {dashboardView: route.dashboardView},
        onSameUrlNavigation: 'reload',
      });
      this.closeSidebar();
      return;
    }

    if (!route.route.includes(':id')) {
      this.router.navigateByUrl(route.route);
      this.closeSidebar();
      return;
    }

    const sectorId = this.selectedSectorId();
    const companyId = this.selectedCompanyId();

    const requiresCompany = route.route.includes(':companyId');
    const requiresUserId = route.route.includes(':userId');
    const missingSector = !sectorId;
    const missingCompany = requiresCompany && !companyId;
    const missingUserId = requiresUserId && !this.currentUserService.appUser()?.id;

    if (missingSector || missingCompany || missingUserId) {
      event.preventDefault();
      event.stopPropagation();
      this.alertService.createAlert(
        new Alert(
          AlertType.WARN,
          '',
          missingUserId
            ? 'De gebruiker wordt nog geladen. Probeer het zo opnieuw.'
            : 'Selecteer eerst een sector voordat je naar deze pagina kunt gaan.',
          false,
        ),
      );
      return;
    }

    this.closeSidebar();
    const buildRoute = this.buildRoute(route);
    this.router.navigateByUrl(buildRoute);
  }

  protected setHoveredRoute(routeKey?: string): void {
    this.hoveredRoute.set(routeKey);
  }

  protected getRouteIcon(route: SidebarRoute & {id: string}, isActive: boolean): string {
    return isActive || this.hoveredRoute() === this.getRouteKey(route)
      ? route.activeIcon || route.icon
      : route.icon;
  }

  protected getRouteKey(route: SidebarRoute & {id: string}): string {
    return route.id;
  }

  protected get visibleRoutes() {
    return getVisibleRoutes(this.currentUserService.roles());
  }

  protected onSelectSector(sectorId: string | null | undefined): void {
    const normalizedId = sectorId || undefined;
    const selectedSector = this.sectors.find(
      (sector) => sector.id === normalizedId,
    );

    this.applySectorSelection(selectedSector);
    this.navigateAfterSectorSelection();
  }

  protected closeProfileModal() {
    this.showProfileModal.set(false);
  }

  protected openProfileModal(event?: Event) {
    if (!this.canOpenProfileModal()) {
      return;
    }

    event?.preventDefault();
    event?.stopPropagation();
    this.showProfileModal.set(true);
  }

  protected canOpenProfileModal(): boolean {
    return this.currentUserService.hasAnyRole([Role.MANAGER, Role.TRAINEE, Role.EMPLOYEE]);
  }

  protected canSelectSector(): boolean {
    return this.currentUserService.hasAnyRole([Role.SUPERADMIN]);
  }

  protected canViewNotifications(): boolean {
    return !this.currentUserService.hasAnyRole([Role.SUPERADMIN]);
  }

  protected buildRoute(route: {route: string}): string {
    if (!route.route.includes(':id')) {
      return route.route;
    }

    const sectorId = this.selectedSectorId();
    const companyId = this.selectedCompanyId();
    const userId = this.currentUserService.appUser()?.id;
    const requiresUserId = route.route.includes(':userId');

    if (requiresUserId && !userId) {
      return '';
    }

    if (requiresUserId && sectorId && userId) {
      return `/sectoren/${sectorId}/ontwikkeling/${userId}`;
    }

    if (companyId && sectorId) {
      return route.route
        .replace(':id', sectorId)
        .replace(':companyId', companyId);
    }
    if (sectorId) {
      return route.route.replace(':id', sectorId);
    }

    return route.route;
  }

  protected selectedSectorColor(): string | undefined {
    const id = this.selectedSectorId();
    if (!id) {
      return undefined;
    }
    return this.sectors.find((sector) => sector.id === id)?.primaryColor;
  }

  private loadSectors(): void {
    if (this.currentUserService.roles().includes(Role.SUPERADMIN)) {
      this.sectorService
        .retrieveSectorsByPage(0, environment.itemsPerPage, [
          'id',
          'name',
          'primaryColor',
          'secondaryColor',
        ])
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (response) => {
            this.sectors = response ?? [];
            this.applyPersistedSelection();
          },
          error: (error) => {
            this.sectors = [];
          },
        });
    }
  }

  private applyPersistedSelection(): void {
    const persistedId = this.selectSectorService.getSelectedSectorId();
    const persistedName =
      this.selectSectorService.getSelectedSectorName?.() ?? '';
    if (!persistedId) {
      if (this.sectors.length > 0) {
        this.applySectorSelection(this.sectors[0]);
        this.navigateIfOnSectorPage();
      }
      return;
    }
    const match = this.sectors.find((sector) => sector.id === persistedId);
    if (match) {
      this.applySectorSelection(match);
      return;
    }
    this.selectedSectorName = persistedName;
  }

  private navigateIfOnSectorPage(): void {
    const sectorId =
      this.selectedSectorId() || this.selectSectorService.getSelectedSectorId();

    if (!sectorId) return;

    const current = this.router.url || '';

    const match = current.match(/^\/sectoren(?:\/[^/]+)?\/([^/]+)(\/.*)?$/);

    if (match) {
      const page = match[1];
      const suffix = match[2] ?? '';

      if (suffix) return;

      this.router.navigateByUrl(`/sectoren/${sectorId}/${page}${suffix}`);
    }
  }

  private navigateAfterSectorSelection(): void {
    const sectorId =
      this.selectedSectorId() || this.selectSectorService.getSelectedSectorId();

    if (!sectorId) return;

    const current = this.router.url || '';
    const isCompanyDetailPage =
      /^\/sectoren\/[^/]+\/bedrijven\/[^/]+(?:\/|$)/.test(current);
    const isUserDevelopmentDetailPage =
      /^\/sectoren\/[^/]+\/ontwikkeling\/[^/?#]+(?:[/?#]|$)/.test(current);

    if (isCompanyDetailPage || isUserDevelopmentDetailPage) {
      this.router.navigateByUrl(`/sectoren/${sectorId}/bedrijven`);
      return;
    }

    this.navigateIfOnSectorPage();
  }

  private applySectorSelection(sector?: SectorDto): void {
    this.selectedSectorName = sector?.name ?? '';
    this.selectSectorService.setSelectedSector(sector);
  }
}
